import { CELL_HEIGHT_MULTIPLIER, CELL_HEIGHT_PX } from '../../constants';
import type { UserInfoVisibility } from '../../store';

// Top block = the name+status sub-column beside the avatar (avatar is 32px, shorter, so
// it doesn't drive height): name text (text-sm ≈ 20px) + status badge (Sm ≈ 16px). The
// avatar column stacks these, so this is their sum, NOT a side-by-side 52px block.
const SIDEBAR_TOP_BLOCK_PX = 40;
// p-4 vertical padding on the sticky sidebar div (16px top + 16px bottom)
const SIDEBAR_PADDING_PX = 32;
// Minimum height to ensure the bottom border is always visible and "User will be
// available on <date>" multiline text (shown when user hasn't joined yet) is never cropped
const SIDEBAR_MIN_HEIGHT_PX = 135;
// Each info row (icon + text-xs)
const INFO_ROW_HEIGHT_PX = 16;
// gap-1.5 between info rows
const INFO_ROW_GAP_PX = 6;
// gap-2.5 (10px) between the avatar column's sections (top block ↔ info ↔ billable)
const SECTION_GAP_PX = 10;
// Billable capacity block: text row (16px) + progress bar (~8px)
const BILLABLE_CAPACITY_HEIGHT_PX = 24;

/**
 * Calculates sidebar height based on which user info fields are visible.
 * Deterministic — no DOM measurement needed. Toggling fields causes one immediate jump.
 */
const getSidebarHeight = (userInfoVisibility: UserInfoVisibility): number => {
  let height = SIDEBAR_TOP_BLOCK_PX + SIDEBAR_PADDING_PX;

  // Count EVERY info row the member card actually renders (see planner-team-member-info.tsx):
  // the fixed rows plus one row per visible custom attribute. Missing any of these here
  // under-computes the row height and clips the card / desyncs it from the timeline row.
  const fixedRows = [
    userInfoVisibility.isManager,
    userInfoVisibility.memberManager,
    userInfoVisibility.department,
    userInfoVisibility.role,
    userInfoVisibility.seniority,
    userInfoVisibility.location,
    userInfoVisibility.capacityPerDay,
    userInfoVisibility.membershipType,
  ].filter(Boolean).length;

  const customAttributeRows = Object.values(userInfoVisibility.customAttributes ?? {}).filter(
    Boolean
  ).length;

  const infoRows = fixedRows + customAttributeRows;

  if (infoRows > 0) {
    height += SECTION_GAP_PX + infoRows * INFO_ROW_HEIGHT_PX + (infoRows - 1) * INFO_ROW_GAP_PX;
  }

  if (userInfoVisibility.billableCapacity) {
    height += SECTION_GAP_PX + BILLABLE_CAPACITY_HEIGHT_PX;
  }

  return Math.max(Math.ceil(height), SIDEBAR_MIN_HEIGHT_PX);
};

const getHeaderHeight = (userInfoVisibility?: UserInfoVisibility): number =>
  userInfoVisibility ? getSidebarHeight(userInfoVisibility) : 199;

/** Assignment-stack height for N levels (the area the bars occupy). */
const getAssignmentsHeight = (levels: number): number =>
  CELL_HEIGHT_PX * levels * CELL_HEIGHT_MULTIPLIER;

/**
 * Standard row height — unchanged original behaviour: the taller of the
 * assignment stack and the sidebar header.
 */
export const getTimelineRowHeight = ({
  maxIntersectedAssignments,
  userInfoVisibility,
  minHeightPx = 0,
}: {
  maxIntersectedAssignments: number;
  userInfoVisibility?: UserInfoVisibility;
  /** Floor from content that needs more room, e.g. bars beside a half-day time-off. */
  minHeightPx?: number;
}) => {
  const headerHeight = getHeaderHeight(userInfoVisibility);
  const assignmentsHeight = getAssignmentsHeight(maxIntersectedAssignments);
  return Math.max(assignmentsHeight, headerHeight, minHeightPx);
};
